/**
 * 押し引き（a3）—— 攻守判断
 *
 * 核心问题：**这一巡该进攻还是该弃和（打安全牌）？**
 *
 * 参考天鳳/雀魂的押し引き理论：
 *
 *   进攻价值（押し）：
 *     · 向听数越少越好（听牌 > 1向听 > 2向听）
 *     · 打点越高越好（满贯 > 满贯未满）
 *     · 巡目越浅越好（还有时间）
 *     · 听牌率：剩余张数 × 巡目余裕
 *
 *   弃和风险（引き）：
 *     · 对手立直数（1家 vs 3家差别巨大）
 *     · 自己打出的牌危险度
 *     · 放铳损失估计
 *
 *   顺位/点数的修正：
 *     · 自家 1 位 → 保守（守成）
 *     · 自家 4 位 → 激进（必须追分）
 *     · 亚东/南场后期 → 更看重顺位
 *
 * 决策：
 *   if (进攻期望 > 弃和期望) → 打牌效最优牌
 *   else                      → 打安全度最高牌
 *
 * ⚠️ 也可以走"中间路线"（部分安全牌 = 兼顾牌效与安全）
 */

import { tileIndex, type Tile } from '../tile.js';
import { shanten } from '../shanten.js';
import { analyzeWaits } from '../wait.js';
import { detectYaku } from '../yaku/index.js';
import { decomposeAll } from '../agari.js';
import { calculateFu } from '../fu.js';
import { scoreFrom } from '../score.js';
import type { WinContext } from '../yaku/types.js';
import { handToCounts, remainingCount } from './rules.js';
import {
  identifyThreats,
  evaluateSafety,
  chooseSafestDiscard,
  type Threat,
  type SafetyEvaluation,
} from './defense.js';
import {
  chooseBestDiscardV2,
  evaluateDiscard,
  nextDoraIndex,
  type DiscardEvaluation,
} from './efficiency.js';
import type { GameState, Seat } from './state.js';
import { seatToWind } from './state.js';

// ════════════════════════════════════════════════════════════════
// 配置
// ════════════════════════════════════════════════════════════════

/** 押し引き配置 */
export interface PushFoldConfig {
  /** 基础押し阈值（越高越保守） */
  readonly pushThreshold: number;
  /** 立直 1 家时的额外权重 */
  readonly riichiPenalty: number;
  /** 巡目权重（越晚越保守） */
  readonly turnWeight: number;
  /** 是否考虑顺位（南场/东4 时启用） */
  readonly useRankAdjust: boolean;
  /** 是否允许"部分安全牌"折中 */
  readonly allowPartial: boolean;
}

export const DEFAULT_PUSH_FOLD: PushFoldConfig = {
  pushThreshold: 1.0,
  riichiPenalty: 0.35,
  turnWeight: 0.03,
  useRankAdjust: true,
  allowPartial: true,
};

// ════════════════════════════════════════════════════════════════
// 手牌价值评估
// ════════════════════════════════════════════════════════════════

/** 手牌价值评估结果 */
export interface HandValue {
  /** 向听数 */
  readonly shanten: number;
  /** 估计打点（番数）*/
  readonly estimatedHan: number;
  /** 打点等级 */
  readonly valueLevel: 'small' | 'medium' | 'large' | 'huge';
  /** 听牌率（0-1，剩余巡目内能听牌的概率估计）*/
  readonly tenpaiRate: number;
  /** 综合价值（0-100）*/
  readonly value: number;
}

/**
 * 评估手牌价值
 *
 * @param state 游戏状态
 * @param seat 自家座位
 * @param assumedWinTile 假设的和了牌（用于估打点；不传则用当前听牌张）
 */
export function evaluateHandValue(
  state: GameState,
  seat: Seat,
  assumedWinTile?: number,
): HandValue {
  const player = state.players[seat];
  const counts = handToCounts(player.hand);
  const meldCount = player.melds.length;
  const s = shanten(counts, meldCount);

  // ── 听牌率估计 ──
  // 简化模型：向听数越低、剩余巡目越多 → 听牌率越高
  const tilesLeft = state.wall.live.length;
  const turnsLeft = Math.floor(tilesLeft / 4);
  const tenpaiRate = estimateTenpaiRate(s, turnsLeft, state, seat);

  // ── 打点估计 ──
  const estimatedHan = estimateHan(state, seat, counts, meldCount, assumedWinTile);

  // ── 打点等级 ──
  let valueLevel: HandValue['valueLevel'] = 'small';
  if (estimatedHan >= 6) valueLevel = 'huge';
  else if (estimatedHan >= 4) valueLevel = 'large';
  else if (estimatedHan >= 2) valueLevel = 'medium';

  // ── 综合价值 ──
  // 向听数贡献（0向听=60分，每多一向听 -15）
  const shantenScore = Math.max(0, 60 - s * 15);
  // 打点贡献（每番 +5，上限 40）
  const hanScore = Math.min(40, estimatedHan * 5);
  const value = Math.round(shantenScore + hanScore);

  return { shanten: s, estimatedHan, valueLevel, tenpaiRate, value };
}

/**
 * 估计剩余巡目内能听牌的概率
 *
 * 简化模型：
 *   · 已听牌 → 1.0
 *   · 1 向听 + 巡目充足 → 0.6
 *   · 2 向听 → 0.3
 *   · 更多 → 递减
 */
function estimateTenpaiRate(
  shanten: number,
  turnsLeft: number,
  state: GameState,
  seat: Seat,
): number {
  if (shanten <= 0) return 1.0;
  if (turnsLeft <= 0) return 0;

  // 基础率（每向听递减）
  const base = [1.0, 0.65, 0.35, 0.18, 0.08, 0.03][Math.min(shanten, 5)] ?? 0.01;
  // 巡目修正（剩 8 巡以上不减，越少越打折扣）
  const turnFactor = Math.min(1, turnsLeft / 8);
  return base * (0.5 + 0.5 * turnFactor);
}

/**
 * 估计打点（番数）
 *
 * 简化：
 *   · 用当前手牌能组成的最高番役 + 宝牌
 *   · 未听牌时给保守估计
 */
function estimateHan(
  state: GameState,
  seat: Seat,
  counts: number[],
  meldCount: number,
  assumedWinTile?: number,
): number {
  const player = state.players[seat];

  // 已听牌 → 用实际听牌张 + 役种计算
  if (shanten(counts, meldCount) === 0) {
    const winTile = assumedWinTile ?? analyzeWaits(counts, player.melds).waits[0]?.tile;
    if (winTile !== undefined) {
      const test = [...counts];
      test[winTile]++;
      const ds = decomposeAll(test, player.melds);
      if (ds.length > 0) {
        const winCtx: WinContext = {
          roundWind: state.round.roundWind,
          seatWind: seatToWind(seat, state.round),
          winMethod: 'ron',
          isRiichi: player.riichi,
          dora: { doraIndicators: state.wall.doraIndicators.map(tileIndex) },
          winTile,
          honba: state.round.honba,
        };
        try {
          const result = detectYaku({
            decomposition: ds[0],
            counts: test,
            allTiles: test,
            meldCount,
            isClosed: player.melds.filter((m) => m.open).length === 0,
            winTile,
            context: winCtx,
          });
          return Math.min(13, result.totalHan + result.doraHan);
        } catch {
          // 忽略：无法判定时给保守估计
        }
      }
    }
  }

  // 未听牌 → 按牌型估算
  let estimate = 1; // 至少 1 番（有役才行，这里粗略）

  // 宝牌（用 nextDoraIndex 从指示牌算出实际宝牌）
  for (const ind of state.wall.doraIndicators) {
    const doraTile = nextDoraIndex(tileIndex(ind));
    estimate += counts[doraTile] ?? 0;
  }

  // 门清加分（立直/门清自摸的可能）
  if (player.melds.length === 0) estimate += 1;

  // 断幺九可能
  const hasYaochu = counts.some((c, i) => c > 0 && (i % 9 === 0 || i % 9 === 8 || i >= 27));
  if (!hasYaochu) estimate += 1;

  // 混一色/清一色潜力
  const suitCounts = [0, 0, 0, 0];
  for (let i = 0; i < 34; i++) suitCounts[Math.floor(i / 9)] += counts[i];
  const total = counts.reduce((a, b) => a + b, 0);
  if (total > 0) {
    for (let si = 0; si < 3; si++) {
      const ratio = suitCounts[si] / total;
      if (ratio >= 0.8) estimate += 3;
      else if (ratio >= 0.6) estimate += 1;
    }
  }

  return Math.min(13, estimate);
}

// ════════════════════════════════════════════════════════════════
// 风险估计
// ════════════════════════════════════════════════════════════════

/** 风险估计结果 */
export interface RiskEstimate {
  /** 威胁源 */
  readonly threats: readonly Threat[];
  /** 立直家数 */
  readonly riichiCount: number;
  /** 平均危险度（0-1）*/
  readonly dangerLevel: number;
  /** 放铳损失估计（点数）*/
  readonly estimatedLoss: number;
}

/**
 * 估计当前的风险
 */
export function estimateRisk(state: GameState, seat: Seat): RiskEstimate {
  const threats = identifyThreats(state, seat);
  const riichiCount = threats.filter((t) => t.riichi).length;

  // 危险度：立直家数 + 副露家的威胁
  let dangerLevel = 0;
  for (const t of threats) dangerLevel += t.weight;
  dangerLevel = Math.min(1, dangerLevel / 2); // 归一化（2 家立直 = 1.0）

  // 放铳损失估计：
  //   · 立直 + 无副露 → 平均 3900（子家满贯未满）
  //   · 立直 + 副露 → 平均 5200
  //   · 无立直 + 副露 → 平均 2600
  let estimatedLoss = 0;
  for (const t of threats) {
    if (t.riichi && t.melds > 0) estimatedLoss += 5200;
    else if (t.riichi) estimatedLoss += 3900;
    else estimatedLoss += 2600;
  }

  return { threats, riichiCount, dangerLevel, estimatedLoss };
}

// ════════════════════════════════════════════════════════════════
// 顺位修正
// ════════════════════════════════════════════════════════════════

/**
 * 根据顺位调整押し意愿
 *
 * @returns 乘数（>1 = 更激进，<1 = 更保守）
 */
export function rankAdjustment(state: GameState, seat: Seat): number {
  const scores = state.players.map((p) => p.score);
  const myScore = scores[seat];
  const sorted = [...scores].sort((a, b) => b - a);
  const rank = sorted.indexOf(myScore) + 1;

  // 场次越后，顺位越重要
  const isLateGame =
    state.round.roundWind !== 'east' || state.round.roundNumber >= 3;

  if (!isLateGame) return 1.0; // 东场前期：纯牌效

  switch (rank) {
    case 1:
      return 0.7; // 1 位 → 保守（守成）
    case 2:
      return 0.9;
    case 3:
      return 1.15; // 3 位 → 略激进
    case 4:
      return 1.4; // 4 位 → 必须追分
    default:
      return 1.0;
  }
}

// ════════════════════════════════════════════════════════════════
// 押し引き决策
// ════════════════════════════════════════════════════════════════

/** 决策类型 */
export type DecisionType = 'push' | 'fold' | 'partial';

/** 押し引き决策结果 */
export interface PushFoldDecision {
  /** 决策 */
  readonly type: DecisionType;
  /** 推荐打牌 */
  readonly tile: Tile;
  /** 手牌价值 */
  readonly handValue: HandValue;
  /** 风险评估 */
  readonly risk: RiskEstimate;
  /** 押し分数（进攻倾向）*/
  readonly pushScore: number;
  /** 弃和分数（防守倾向）*/
  readonly foldScore: number;
  /** 说明 */
  readonly reason: string;
}

/**
 * 押し引き主决策
 *
 * 算法：
 *   1. 评估手牌价值（向听数 + 打点 + 听牌率）
 *   2. 评估风险（立直家数 + 放铳损失）
 *   3. 顺位修正
 *   4. 比较押し分 vs 弃和分
 */
export function decidePushFold(
  state: GameState,
  seat: Seat,
  config: PushFoldConfig = DEFAULT_PUSH_FOLD,
): PushFoldDecision {
  const handValue = evaluateHandValue(state, seat);
  const risk = estimateRisk(state, seat);

  // ── 无威胁 → 全力进攻 ──
  if (risk.threats.length === 0) {
    const best = chooseBestDiscardV2(state, seat);
    return {
      type: 'push',
      tile: best.tile,
      handValue,
      risk,
      pushScore: 100,
      foldScore: 0,
      reason: '无威胁源，全力进攻',
    };
  }

  // ── 计算押し分 ──
  //
  // 设计：pushScore 是"进攻倾向分"，基准 50，经过各项修正后可能 0-150。
  // ⚠️ 关键：基础分必须 > 0，否则后续的乘法修正（顺位）会失去意义。
  //    （踩过：基础分过低导致 pushScore 为负，×1.4 反而更负 → 顺位反转）
  let pushScore = 50 + handValue.value * 0.5; // 基准 50 + 手牌价值贡献（0-50）

  // 向听数修正：听牌时大幅提升押し意愿
  if (handValue.shanten <= 0) pushScore += 35; // 听牌：强烈倾向进攻
  else if (handValue.shanten === 1) pushScore += 12;
  else if (handValue.shanten === 2) pushScore -= 8;
  else if (handValue.shanten >= 3) pushScore -= 20;

  // 打点修正：大牌更值得冒险
  if (handValue.valueLevel === 'huge') pushScore += 25;
  else if (handValue.valueLevel === 'large') pushScore += 15;
  else if (handValue.valueLevel === 'small') pushScore -= 5;

  // 巡目修正：越晚越保守（最多 -25）
  const turnsUsed = Math.floor((136 - state.wall.live.length) / 4);
  pushScore -= Math.min(25, turnsUsed * config.turnWeight * 8);

  // 立直惩罚（1 家 -15，3 家 -45）
  pushScore -= risk.riichiCount * config.riichiPenalty * 15;

  // ⚠️ 先夹到非负，再乘顺位系数（保证乘法的方向正确）
  pushScore = Math.max(1, pushScore);

  // 顺位修正（>1 更激进，<1 更保守）
  pushScore *= rankAdjustment(state, seat);

  // ── 计算弃和分 ──
  // 弃和 = 打安全牌避免放铳
  // 分数 = 100 - 风险（风险越高，弃和越有价值）
  const foldScore = risk.dangerLevel * 100;

  // 自己的点数越高，越应该弃和
  const foldBonus = state.players[seat].score > 30000 ? 20 : 0;
  const totalFoldScore = foldScore + foldBonus;

  // ── 决策 ──
  const threshold = 100 * config.pushThreshold;

  if (pushScore >= threshold) {
    const best = chooseBestDiscardV2(state, seat);
    return {
      type: 'push',
      tile: best.tile,
      handValue,
      risk,
      pushScore: Math.round(pushScore),
      foldScore: Math.round(totalFoldScore),
      reason: `进攻：${handValue.shanten}向听 / ${handValue.valueLevel} / ${risk.riichiCount}家立直`,
    };
  }

  // ── 折中路：找"既安全又不太差"的牌 ──
  if (config.allowPartial && handValue.shanten <= 1) {
    const partial = choosePartialDiscard(state, seat);
    if (partial) {
      return {
        type: 'partial',
        tile: partial.tile,
        handValue,
        risk,
        pushScore: Math.round(pushScore),
        foldScore: Math.round(totalFoldScore),
        reason: `折中：${partial.reason}`,
      };
    }
  }

  // ── 弃和：打最安全的牌 ──
  const safest = chooseSafestDiscard(state, seat);
  return {
    type: 'fold',
    tile: safest.tile,
    handValue,
    risk,
    pushScore: Math.round(pushScore),
    foldScore: Math.round(totalFoldScore),
    reason: `弃和：安全度 ${safest.safety}（${safest.level}）`,
  };
}

/**
 * 折中打牌：在"安全度达标"的牌里选牌效最好的
 *
 * 思路：如果手牌还行（1向听以内），不完全弃和，
 *       而是在"安全度 ≥ 阈值"的候选中选牌效最优的。
 */
function choosePartialDiscard(
  state: GameState,
  seat: Seat,
  safetyFloor = 60,
): { tile: Tile; reason: string } | null {
  const player = state.players[seat];
  const threats = identifyThreats(state, seat);
  const seen = new Set<number>();

  let best: { tile: Tile; effScore: number; safety: number } | null = null;

  for (const tile of player.hand) {
    const idx = tileIndex(tile);
    if (seen.has(idx)) continue;
    seen.add(idx);

    const safety = evaluateSafety(state, seat, tile, threats);
    if (safety.safety < safetyFloor) continue; // 不够安全

    const eff = evaluateDiscardSafe(state, seat, tile);
    if (!best || eff > best.effScore) {
      best = { tile, effScore: eff, safety: safety.safety };
    }
  }

  if (!best) return null;
  return {
    tile: best.tile,
    reason: `安全度 ${best.safety}，牌效分 ${best.effScore.toFixed(0)}`,
  };
}

/** 安全地评估打牌的牌效（不抛异常）*/
function evaluateDiscardSafe(state: GameState, seat: Seat, tile: Tile): number {
  try {
    return evaluateDiscard(state, seat, tile).score;
  } catch {
    return 0;
  }
}

// ════════════════════════════════════════════════════════════════
// 展示
// ════════════════════════════════════════════════════════════════

/** 决策类型中文 */
export function decisionTypeName(t: DecisionType): string {
  return { push: '进攻', fold: '弃和', partial: '折中' }[t];
}

/** 格式化决策 */
export function formatDecision(d: PushFoldDecision): string {
  const name = `${d.tile.num}${d.tile.suit}`;
  return `[${decisionTypeName(d.type)}] 打 ${name} | 押${d.pushScore} vs 弃${d.foldScore} | ${d.reason}`;
}
